/**
 * Base API client — shared between web and React Native.
 *
 * Each surface calls `configureClient({ baseUrl })` once at boot:
 *  - web reads `import.meta.env.VITE_API_URL`
 *  - mobile reads `tenants/<id>/app.config.ts`'s `apiUrl`
 *
 * `request<T>(method, path, opts)` injects `Authorization: Bearer …`
 * from `@aws-amplify/auth`'s session on every authed request and
 * returns either `{ data }` or `{ error }` so callers can pattern-match
 * without try/catch.
 */

import { fetchAuthSession } from "@aws-amplify/auth";

import type { ApiError } from "./types.gen";

let API_BASE_URL = "";

export function configureClient(opts: { baseUrl: string }): void {
  API_BASE_URL = opts.baseUrl;
}

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  auth?: boolean;
  /** Extra request headers (Idempotency-Key, custom tracing, etc.). */
  headers?: Record<string, string>;
  /**
   * If true, propagate `meta` from the response envelope back to the
   * caller along with `data`. Used by paginated endpoints.
   */
  includeMeta?: boolean;
}

async function getBearerToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    // Use the ID token rather than the access token. Cognito access
    // tokens do not carry custom attributes (`custom:tenant_id`,
    // `custom:role`) by default — the require_auth helper on the
    // backend reads those claims to scope every request to its
    // tenant. The ID token includes them. API Gateway's JWT
    // authorizer accepts both formats; for ID tokens it validates
    // `aud` against the user pool client ID configured on the
    // authorizer.
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!API_BASE_URL) {
    throw new Error(
      "API client not configured. Call configureClient({ baseUrl }) at app boot.",
    );
  }
  const url = new URL(path, API_BASE_URL);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T; meta?: Record<string, unknown> } | { error: ApiError }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Requested-With": "fetch",
    ...(opts.headers ?? {}),
  };

  if (opts.auth) {
    const token = await getBearerToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "omit",
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  const url = buildUrl(path, opts.query);
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    return {
      error: {
        error: {
          code: "INTERNAL",
          message: err instanceof Error ? err.message : "Network error",
        },
        meta: {},
      },
    };
  }

  const text = await response.text();
  const json = text ? safeParse(text) : null;

  if (!response.ok) {
    return { error: asApiError(json, response.status) };
  }
  const envelope = json as { data: T; meta?: Record<string, unknown> } | T;
  const data =
    envelope && typeof envelope === "object" && "data" in envelope
      ? (envelope as { data: T }).data
      : (envelope as T);
  if (opts.includeMeta && envelope && typeof envelope === "object" && "meta" in envelope) {
    return { data, meta: (envelope as { meta?: Record<string, unknown> }).meta };
  }
  return { data };
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Normalize an arbitrary error body into the ApiError envelope.
 * Lambdas behind API GW that crash unhandled return
 * `{"message": "Internal Server Error"}` (no nested `.error` field),
 * so an unconditional cast is unsound — callers reading
 * `result.error.error.message` would crash. */
function asApiError(body: unknown, status: number): ApiError {
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    typeof (body as { error: unknown }).error === "object" &&
    (body as { error: { message?: unknown } }).error !== null &&
    typeof (body as { error: { message?: unknown } }).error.message === "string"
  ) {
    return body as ApiError;
  }
  // Pydantic 422 — `{statusCode: 422, detail: [{loc, msg, ...}, ...]}`
  // from aws_lambda_powertools' validation layer. Surface the first
  // field-level error so the user sees "ar_linear_payload.length_inches:
  // Input should be greater than 0" instead of "Request failed (422)".
  if (
    status === 422 &&
    body &&
    typeof body === "object" &&
    "detail" in body &&
    Array.isArray((body as { detail: unknown }).detail) &&
    (body as { detail: unknown[] }).detail.length > 0
  ) {
    const first = (body as { detail: unknown[] }).detail[0];
    if (first && typeof first === "object" && "msg" in first) {
      const loc = (first as { loc?: unknown }).loc;
      const msg = (first as { msg?: unknown }).msg;
      const locStr =
        Array.isArray(loc) && loc.length > 1
          ? loc.slice(1).join(".")
          : Array.isArray(loc)
            ? loc.join(".")
            : "";
      if (typeof msg === "string" && msg.length > 0) {
        return {
          error: {
            code: "INVALID_INPUT",
            message: locStr ? `${locStr}: ${msg}` : msg,
          },
          meta: {},
        };
      }
    }
  }

  // API GW unhandled-error envelope OR an unexpected shape — fall back
  // and pull the body's `message` if present so the user sees something
  // useful instead of a generic status string.
  const fallback = defaultError(status);
  if (body && typeof body === "object" && "message" in body) {
    const msg = (body as { message?: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return {
        error: { code: fallback.error.code, message: msg },
        meta: {},
      };
    }
  }
  return fallback;
}

function defaultError(status: number): ApiError {
  const code =
    status === 401
      ? "UNAUTHENTICATED"
      : status === 403
        ? "FORBIDDEN"
        : status === 404
          ? "NOT_FOUND"
          : status === 409
            ? "CONFLICT"
            : status === 429
              ? "RATE_LIMITED"
              : "INTERNAL";
  return {
    error: { code, message: `Request failed with status ${status}` },
    meta: {},
  };
}
